import React, { useState } from 'react';
import styled from 'styled-components';
import toast from '~~elements/Toast';
import Button from '~~elements/Button';
import Loading from '~~elements/Loading';
import { CPBLResource, FormalGameResource } from '~~apis/resource';
import { GameSectionDiv } from '../styled';

const StyledDiv = styled.div`
  width: 100%;
  .dedupe--actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-wrap: wrap;
    margin: 0.5rem 0;
  }
  table {
    width: 100%;
    font-size: 0.9rem;
    tr {
      border-bottom: 1px solid gray;
    }
    td,
    th {
      padding: 0.2rem 0.4rem;
      vertical-align: top;
    }
  }
`;

const MOVING_LABELS = {
  formal_game_players: '出賽',
  formal_batting_snapshots: '打擊快照',
  formal_pitching_snapshots: '投球快照',
  game_players: '一般出賽',
  formal_team_player_trans: '異動',
};

// 只有 Rebas 管理員 + CPBL 才能使用；合併同一賽季球隊中重複登錄的同一位球員（多因換背號）
const CPBLSeasonTeamPlayerDedupeSection = ({ tournament }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);

  const handlePreview = () => {
    setLoading(true);
    setApplied(null);
    CPBLResource.dedupeSeasonTeamPlayers({ seasonUniqid: tournament.uniqid })
      .then(({ data }) => {
        setPreview(data.groups);
      })
      .catch((e) => {
        console.error(e);
        toast('無法取得重複名單，請稍候再試', { status: 'error', second: 2 });
      })
      .finally(() => setLoading(false));
  };

  const handleApply = () => {
    if (!window.confirm(`確定要合併 ${preview.length} 組重複球員嗎？此動作無法復原`)) {
      return;
    }
    setLoading(true);
    CPBLResource.dedupeSeasonTeamPlayers({ seasonUniqid: tournament.uniqid, apply: true })
      .then(({ data }) => {
        const failed = data.groups.filter((g) => !g.applied);
        setApplied(data.groups);
        setPreview(null);
        if (failed.length > 0) {
          toast(`合併完成，但有 ${failed.length} 組失敗，請查看下方結果`, { status: 'error', second: 3 });
          return;
        }
        toast('合併成功，請接著重算累計成績', { status: 'success', second: 2 });
      })
      .catch((e) => {
        console.error(e);
        toast('合併失敗，請重整後重新檢查', { status: 'error', second: 2 });
      })
      .finally(() => setLoading(false));
  };

  const handleAccumulate = () => {
    toast('請兩分鐘後至前台確認，勿重複點擊！', { status: 'info', second: 5 });
    FormalGameResource.updateAccumulate({ seasonUniqid: tournament.uniqid }).catch((e) => {
      console.error(e);
    });
  };

  const renderTable = (groups) => (
    <table>
      <thead>
        <tr>
          <th>球隊</th>
          <th>球員</th>
          <th>背號（舊 → 新）</th>
          <th>保留 / 合併 ID</th>
          <th>搬移資料</th>
          {applied && <th>結果</th>}
        </tr>
      </thead>
      <tbody>
        {groups.map((group) => (
          <tr key={`${group.season_team}-${group.player_id}`}>
            <td>{group.season_team}</td>
            <td>
              {group.player_name}
              <br />
              {group.player_uniqid}
            </td>
            <td>
              {group.numbers.join(' → ')}
              <br />
              {`合併後：#${group.updates.number}`}
            </td>
            <td>
              {`保留 ${group.keep_id}`}
              <br />
              {`合併 ${group.merge_ids.join(', ')}`}
            </td>
            <td>
              {Object.entries(group.moving)
                .filter(([, count]) => count > 0)
                .map(([key, count]) => `${MOVING_LABELS[key] ?? key} ${count}`)
                .join('、') || '無'}
            </td>
            {applied && <td>{group.applied ? '成功' : `失敗：${group.error ?? ''}`}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <GameSectionDiv>
      <h3 className="section--title">{`重複登錄球員合併（${tournament.title}）`}</h3>
      <StyledDiv>
        <p>同一賽季球隊中，同一位球員有多筆登錄（例如換背號後重複建立）時，合併為一筆並保留最新背號。</p>
        <div className="dedupe--actions">
          {loading ? (
            <Loading width="100px" />
          ) : (
            <>
              <Button width="10rem" color="blue" onClick={handlePreview}>
                檢查重複球員
              </Button>
              {preview && preview.length > 0 && (
                <Button width="10rem" color="red" onClick={handleApply}>
                  {`確認合併 ${preview.length} 組`}
                </Button>
              )}
              {applied && (
                <Button width="10rem" color="red" onClick={handleAccumulate}>
                  重算累計成績
                </Button>
              )}
            </>
          )}
        </div>
        {preview && preview.length === 0 && <p>沒有重複登錄的球員</p>}
        {preview && preview.length > 0 && renderTable(preview)}
        {applied && (
          <>
            <p>合併結果（完成後請按「重算累計成績」，數據快照才會正確）</p>
            {renderTable(applied)}
          </>
        )}
      </StyledDiv>
    </GameSectionDiv>
  );
};

export default CPBLSeasonTeamPlayerDedupeSection;
